import api from './api'
import type { JournalEntryLine } from './accounting'

export const ALL_STORES_ID = '__all__'

export interface CloverGiftCardReconUnpaidRow {
  classification: string
  evidence_note?: string
  order_id?: string | null
  override_key: string
  last4: string
  amount: number | string
  timestamp_ct: string
  source: string
  processor_row: Record<string, unknown>
}

export interface CloverGiftCardReconMatchedRow {
  order_id: string
  order_line_amount: number | string
  processor_row: Record<string, unknown>
}

export interface GiftCardReconResult {
  matched: CloverGiftCardReconMatchedRow[]
  unpaid: CloverGiftCardReconUnpaidRow[]
  sold_but_not_activated: number | string
  adjustments: Record<string, unknown>[]
  service_fees: Record<string, unknown>[]
  total_adjustments: number | string
  total_service_fees: number | string
  warnings: string[]
  classifications: Record<string, string>
}

export interface CloverFetchRequest {
  store_name: string
  report_date: string
}

export interface CloverGiftCardActivityRow {
  location_id: string
  account_last4: string
  account_hash: string
  promo_code: string
  request: string
  amount: number | string
  response_code: string
  source: string
  timestamp: string
  timestamp_ct: string
  is_financial: boolean
}

export interface CloverGiftCardActivity {
  report_available: boolean
  report_as_of?: string | null
  activations: CloverGiftCardActivityRow[]
  reloads: CloverGiftCardActivityRow[]
  redemptions: CloverGiftCardActivityRow[]
  voids: CloverGiftCardActivityRow[]
  adjustments: CloverGiftCardActivityRow[]
  service_fees: CloverGiftCardActivityRow[]
  balance_inquiries: CloverGiftCardActivityRow[]
  total_activations: number | string
  total_reloads: number | string
  total_redemptions: number | string
  total_voids: number | string
  total_adjustments: number | string
  total_service_fees: number | string
  error?: string | null
}

export interface CloverTenderType {
  name: string
  amount: number | string
}

export interface CloverSalesReport {
  store_name: string
  merchant_id: string
  report_date: string
  gross_sales: number | string
  discounts: number | string
  refunds: number | string
  net_sales: number | string
  gift_card_activations: number | string
  overpayments: number | string
  taxes_expected: number | string
  taxes_collected: number | string
  tips: number | string
  amount_collected: number | string
  unpaid_balance: number | string
  payments_for_other_orders: number | string
  tender_types: CloverTenderType[]
}

export interface CloverDepositReport {
  store_name: string
  merchant_id: string
  report_date: string
  amount_submitted: number | string
  fees_adjustments: number | string
  processing_fees: number | string
  deposit_adjustment: number | string
  chargebacks: number | string
  amount_transferred: number | string
  transfers: number
}

export interface CloverFetchResponse {
  sales_report: CloverSalesReport
  deposit_report: CloverDepositReport
  sales_report_date: string
  deposit_report_date: string
  gift_card_activity?: CloverGiftCardActivity | null
}

export interface CloverDraftRequest {
  store_name: string
  report_date: string
  sales_report: CloverSalesReport
  deposit_report: CloverDepositReport
  gift_card_classifications?: Record<string, string>
  gift_card_activity?: CloverGiftCardActivity | null
}

export interface CloverDraftLineMeta {
  classification?: string
  hint?: string
}

export interface CloverDraftResponse {
  id: number
  lines: JournalEntryLine[]
  line_meta?: CloverDraftLineMeta[]
  warnings: string[]
  private_note?: string
  doc_number: string
  txn_date: string
  source_data: Record<string, unknown>
  total_debits: number | string
  total_credits: number | string
  status: string
  qbo_url?: string | null
  qbo_entity_id?: string | null
  needs_qbo_update?: boolean
  gift_card_recon?: GiftCardReconResult | null
}

export interface CloverDraftActionResponse {
  draft: CloverDraftResponse
  regenerated: boolean
}

export interface CloverPushQboUpdateRequest {
  gift_card_classifications?: Record<string, string>
  refresh_gift_cards?: boolean
  force_regenerate?: boolean
}

export interface CloverJournalEntryDateCount {
  date: string
  je_count: number
  complete?: boolean
  /** False when the date is still inside the 2-day Clover booking lag. */
  actionable?: boolean
  posted_store_names?: string[]
  expected_store_names?: string[]
}

export interface CloverNavStatusResponse {
  id: number
  ran_at: string
  allocate_incomplete_count: number
  gift_card_incomplete_count: number
}

export async function fetchCloverReports(
  data: CloverFetchRequest,
  options?: { refresh_gift_cards?: boolean },
): Promise<CloverFetchResponse> {
  const response = await api.post<CloverFetchResponse>('/clover-reports/fetch', data, {
    params: options?.refresh_gift_cards ? { refresh_gift_cards: true } : undefined,
  })
  return response.data
}

export async function createCloverDraft(data: CloverDraftRequest): Promise<CloverDraftResponse> {
  const response = await api.post<CloverDraftResponse>('/clover-reports/draft', data)
  return response.data
}

export async function pushCloverDraftQboUpdate(
  draftId: number,
  body?: CloverPushQboUpdateRequest,
): Promise<CloverDraftActionResponse> {
  const response = await api.post<CloverDraftActionResponse>(
    `/clover-reports/drafts/${draftId}/push-qbo-update`,
    body ?? {},
  )
  return response.data
}

export async function fetchCloverJournalEntryHistory(): Promise<CloverJournalEntryDateCount[]> {
  const response = await api.get<CloverJournalEntryDateCount[]>(
    '/clover-reports/journal-entry-history',
  )
  return response.data
}

export async function fetchCloverDateStatuses(): Promise<CloverJournalEntryDateCount[]> {
  const response = await api.get<CloverJournalEntryDateCount[]>('/clover-reports/date-statuses')
  return response.data
}

export async function refreshCloverDateStatuses(): Promise<CloverJournalEntryDateCount[]> {
  const response = await api.post<CloverJournalEntryDateCount[]>(
    '/clover-reports/date-statuses/refresh',
  )
  return response.data
}

export async function getCloverNavStatus(
  opts?: { refresh?: boolean },
): Promise<CloverNavStatusResponse> {
  const response = await api.get<CloverNavStatusResponse>('/clover-reports/nav-status', {
    params: opts?.refresh ? { refresh: true } : undefined,
  })
  return response.data
}
